<template>
  <v-sheet class="pa-5">
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 200px;">
      <v-progress-circular color="primary" indeterminate />
    </div>
    <template v-else-if="device">
      <div class="d-flex align-center ga-2">
        <template v-if="editingName">
          <v-text-field
            v-model="editedName"
            autofocus
            density="compact"
            hide-details
            variant="outlined"
            @keyup.enter="saveName"
            @keyup.escape="cancelEditName"
          />
          <v-btn
            color="primary"
            icon="mdi-check"
            size="small"
            variant="text"
            @click="saveName"
          />
          <v-btn icon="mdi-close" size="small" variant="text" @click="cancelEditName" />
        </template>
        <template v-else>
          <h2>{{ device.name }}</h2>
          <v-btn icon="mdi-pencil" size="small" variant="text" @click="startEditName" />
        </template>
      </div>

      <div class="mt-3">
        <div class="d-flex justify-space-between align-center mb-1">
          <span class="text-body-2 text-medium-emphasis">
            <v-icon class="mr-1" :color="storageColor" size="small">mdi-sd</v-icon>
            Espace de stockage utilisé
          </span>
          <span class="text-body-2 font-weight-bold">{{ Math.round(device.used_storage_percent) }} %</span>
        </div>
        <v-progress-linear
          :color="storageColor"
          height="10"
          :model-value="device.used_storage_percent"
          rounded
        />
      </div>

      <v-tabs v-model="activeTab" class="mt-4" color="primary">
        <v-tab value="messages">Messages audios</v-tab>
        <v-tab value="config">Options</v-tab>
        <v-tab value="share">Partage</v-tab>
        <v-tab value="infos">Informations</v-tab>
      </v-tabs>

      <v-divider />

      <v-tabs-window v-model="activeTab">
        <v-tabs-window-item value="messages">
          <the-device-messages-component ref="messagesComponentRef" :device-id="deviceId" @update:recording="v => isRecording = v" />
        </v-tabs-window-item>

        <v-tabs-window-item value="config">
          <the-device-configuration-component ref="configComponentRef" :device="device" @deleted="emit('deleted')" />
        </v-tabs-window-item>

        <v-tabs-window-item value="share">
          <the-device-sharing-component :device="device" @update:device="d => device = d" />
        </v-tabs-window-item>

        <v-tabs-window-item value="infos">
          <the-device-informations-component :device="device" />
        </v-tabs-window-item>
      </v-tabs-window>
    </template>
  </v-sheet>
</template>

<script setup lang="ts">
  import type { Device } from '@/model/device.ts'
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { deviceRepository } from '@/api/repository/device.repository.ts'
  import TheDeviceConfigurationComponent from '@/components/device-components/TheDeviceConfigurationComponent.vue'
  import TheDeviceInformationsComponent from '@/components/device-components/TheDeviceInformationsComponent.vue'
  import TheDeviceMessagesComponent from '@/components/device-components/TheDeviceMessagesComponent.vue'
  import TheDeviceSharingComponent from '@/components/device-components/TheDeviceSharingComponent.vue'
  import { notifyApiError } from '@/utils/error.utils.ts'

  const route = useRoute()
  const router = useRouter()

  const VALID_TABS = new Set(['messages', 'config', 'share', 'infos'])
  const initialTab = typeof route.query.tab === 'string' && VALID_TABS.has(route.query.tab) ? route.query.tab : 'messages'

  const device = ref<Device | null>(null)
  const loading = ref(true)
  const activeTab = ref(initialTab)
  const isRecording = ref(false)
  const configComponentRef = ref<InstanceType<typeof TheDeviceConfigurationComponent> | null>(null)
  const messagesComponentRef = ref<InstanceType<typeof TheDeviceMessagesComponent> | null>(null)

  watch(activeTab, tab => {
    // Différé pour ne pas interférer avec la transition visuelle de l'onglet.
    nextTick(() => {
      router.replace({ query: { ...route.query, tab } })
      refreshDevice()
      if (tab === 'config') {
        configComponentRef.value?.loadOptions()
      }
      if (tab === 'messages') {
        messagesComponentRef.value?.refreshMessages()
      }
    })
  })
  const editingName = ref(false)
  const editedName = ref('')

  function startEditName () {
    editedName.value = device.value?.name ?? ''
    editingName.value = true
  }

  function cancelEditName () {
    editingName.value = false
  }

  async function saveName () {
    const trimmed = editedName.value.trim()
    if (!trimmed || !device.value || trimmed === device.value.name) {
      cancelEditName()
      return
    }
    try {
      device.value = await deviceRepository.updateDevice(props.deviceId, { name: trimmed, family_groups: [] })
      editingName.value = false
      emit('update:device', device.value)
    } catch (error) {
      notifyApiError('Erreur lors du renommage du bouton', error)
    }
  }

  const storageColor = computed(() => {
    const percent = device.value?.used_storage_percent ?? 0
    if (percent >= 90) return 'error'
    if (percent >= 70) return 'warning'
    return 'success'
  })

  const props = defineProps<{
    deviceId: string
  }>()

  const emit = defineEmits<{
    'update:device': [device: Device]
    'deleted': []
  }>()

  async function loadDevice () {
    loading.value = true
    try {
      device.value = await deviceRepository.getDevice(props.deviceId)
    } catch (error) {
      notifyApiError('Impossible de charger les informations du bouton', error)
      console.error('Failed to load device', error)
    } finally {
      loading.value = false
    }
  }

  async function refreshDevice () {
    if (isRecording.value) return
    try {
      device.value = await deviceRepository.getDevice(props.deviceId)
    } catch {
      // silently ignore refresh errors
    }
  }

  const RELOAD_INTERVAL_MS = 120_000
  let reloadInterval: ReturnType<typeof setInterval> | null = null

  onMounted(() => {
    loadDevice()
    reloadInterval = setInterval(refreshDevice, RELOAD_INTERVAL_MS)
  })

  onBeforeUnmount(() => {
    if (reloadInterval) {
      clearInterval(reloadInterval)
    }
  })
</script>

<style scoped>
:deep(.v-tabs-window) {
  overflow: visible !important;
}
</style>
